import { useState } from 'react'
import { Button, FormLayout } from 'components/common'
import { Switch } from 'components/common/switch'
import { FieldValues, UseFormSetValue } from 'react-hook-form'
import { LogConfig, LogModuleKey } from 'types/configuration'
import { LOG_MODULES, buildLogConfig } from './utils'

type Props = {
  setValue: UseFormSetValue<FieldValues>
  logConfig?: LogConfig
}

/**
 * Log Configuration card. Renders one toggle per S3-log module. The toggle
 * controls only whether the heavy S3 log file is uploaded — the DB log row is
 * always written, so each module's Logs page keeps showing the event.
 *
 * State is managed locally (like the shared Dropdown component) and mirrored
 * into the parent react-hook-form via `setValue('logConfig', ...)`, so the
 * single Save button on the Configuration form persists it with the rest of
 * the config through `PATCH /config/:id`.
 */
const LogConfigSection = ({ setValue, logConfig }: Props) => {
  const [values, setValues] = useState<Record<LogModuleKey, boolean>>(() =>
    buildLogConfig(logConfig),
  )

  const sync = (next: Record<LogModuleKey, boolean>) => {
    setValues(next)
    setValue('logConfig', next)
  }

  const setAll = (enabled: boolean) =>
    sync(
      LOG_MODULES.reduce(
        (acc, { key }) => {
          acc[key] = enabled
          return acc
        },
        {} as Record<LogModuleKey, boolean>,
      ),
    )

  return (
    <>
      <h3 className="title mt-3">Log Configuration</h3>
      <FormLayout>
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-3xl text-[13px] text-gray-500">
              When a log is turned off, its detailed file is no longer stored,
              but the log entry still appears in its Logs page.
            </p>
            <div className="flex shrink-0 gap-2">
              <Button
                type="button"
                variant="outline"
                size="small"
                buttonText="Enable all"
                onClick={() => setAll(true)}
              />
              <Button
                type="button"
                variant="outline"
                size="small"
                buttonText="Disable all"
                onClick={() => setAll(false)}
              />
            </div>
          </div>

          <div className="mt-6 grid gap-x-10 gap-y-4 mid-md:grid-cols-3">
            {LOG_MODULES.map(({ key, label }) => (
              <div
                key={key}
                className="flex items-center justify-between gap-3"
              >
                <label
                  htmlFor={`logConfig-${key}`}
                  className="text-sm text-gray-700"
                >
                  {label}
                </label>
                <Switch
                  id={`logConfig-${key}`}
                  checked={values[key]}
                  onChange={(checked) => sync({ ...values, [key]: checked })}
                />
              </div>
            ))}
          </div>
        </div>
      </FormLayout>
    </>
  )
}

export default LogConfigSection
